export interface IClipboardResult {
  status: 'success' | 'error';
}

const copyByTextarea = (text: string) => {
  const textarea = document.createElement('textarea');
  const activeElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
  const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;

  const handleCopy = (event: ClipboardEvent) => {
    if (!event.clipboardData) return;

    event.clipboardData.setData('text/plain', text);
    event.preventDefault();
  };

  try {
    textarea.value = text;
    textarea.setAttribute('readonly', 'readonly');
    textarea.style.cssText = [
      'position: fixed',
      'left: -9999px',
      `top: ${scrollTop}px`,
      'width: 1px',
      'height: 1px',
      'opacity: 0',
      'font-size: 16px'
    ].join(';');

    document.body.appendChild(textarea);
    textarea.focus({ preventScroll: true });
    textarea.select();
    textarea.setSelectionRange(0, text.length);
    document.addEventListener('copy', handleCopy);

    return document.execCommand('copy');
  } finally {
    document.removeEventListener('copy', handleCopy);
    textarea.blur();
    textarea.remove();
    activeElement?.focus?.({ preventScroll: true });
    window.scrollTo(scrollLeft, scrollTop);
  }
};

const copyByClipboardApi = async (text: string) => {
  if (!window.isSecureContext || !navigator.clipboard?.writeText) return false;

  await navigator.clipboard.writeText(text);
  return true;
};

const emitClipboardResult = (isSuccess: boolean, callback?: (result: IClipboardResult) => void) => {
  const result: IClipboardResult = { status: isSuccess ? 'success' : 'error' };

  callback?.(result);
  return result;
};

export async function clipboard(text: string, callback?: (result: IClipboardResult) => void): Promise<IClipboardResult> {
  if (!text) return emitClipboardResult(false, callback);

  try {
    if (copyByTextarea(text)) return emitClipboardResult(true, callback);
  } catch {
    // 兼容复制失败后继续尝试 Clipboard API
  }

  try {
    return emitClipboardResult(await copyByClipboardApi(text), callback);
  } catch {
    return emitClipboardResult(false, callback);
  }
}

// 防抖, 最后一次说话算数（停止操作后才执行）
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number,
  options: { immediate?: boolean } | boolean = { immediate: false }
): (...args: Parameters<T>) => Promise<ReturnType<T>> {
  let timeout: ReturnType<typeof setTimeout> | null = null;

  if (typeof options === 'boolean') {
    options = { immediate: options };
  }

  return function (this: ThisParameterType<T>, ...args: Parameters<T>): any {
    const context = this;
    const callNow = Boolean(options?.immediate) && !timeout;

    if (timeout) {
      clearTimeout(timeout);
    }

    timeout = setTimeout(() => {
      timeout = null;
      // 非立即执行模式：等待结束后以最后一次的参数执行（真正的尾沿防抖）
      if (!options?.immediate) {
        func.apply(context, args);
      }
    }, wait);

    if (callNow) {
      func.apply(context, args);
    }
  };
}

//节流, 限制发言频率（固定时间内只执行一次）
export function throttle<T extends (...args: any[]) => any>(
  func: T,
  limit: number,
  leadingOnly: boolean = false
): (...args: Parameters<T>) => any {
  let lastFunc: ReturnType<typeof setTimeout> | null = null;
  let lastRan: number | null = null;

  return function (this: ThisParameterType<T>, ...args: Parameters<T>) {
    const context = this;
    const now = Date.now();

    if (lastRan === null || now - lastRan >= limit) {
      if (lastFunc) {
        clearTimeout(lastFunc);
        lastFunc = null;
      }
      func.apply(context, args);
      lastRan = now;
    } else if (!leadingOnly) {
      if (!lastFunc) {
        lastFunc = setTimeout(
          () => {
            func.apply(context, args);
            lastRan = Date.now();
            lastFunc = null;
          },
          limit - (now - lastRan)
        );
      }
    }
  };
}

/* 将px转为rem */
export function cssUnit2Rem(value?: string | number, ratio: number = 37.5): string {
  if (value === undefined || value === '') return '0rem';
  const str = String(value).trim();
  const normalized = str.startsWith('.') ? '0' + str : str;

  // 纯数字 或 px
  const match = normalized.match(/^(-?\d+(\.\d+)?)(px)?$/);
  if (!match) return str;
  const num = parseFloat(match[1]);
  return num / ratio + 'rem';
}

/* rem 布局的设计基准：375 设计稿宽对应根字号 37.5px */
export const DESIGN_BASE = 37.5;

/* 取当前根字号（px），未初始化时回落到设计基准 */
export function getRootFontSize(): number {
  const root = parseFloat(getComputedStyle(document.documentElement).fontSize);
  return Number.isFinite(root) && root > 0 ? root : DESIGN_BASE;
}

/*
 * 生成"设计稿px → 设备px"换算器，供 canvas 等不认 rem、必须吃数值 px 的场景使用。
 * 根字号由 useAppInit 设置一次（setFontSize 的 resize 监听未启用），所以这里快照一次即可；
 * 若将来根字号需要跟随窗口变化，必须重新创建换算器。
 */
export function createPxConverter(): (designPx: number) => number {
  const scale = getRootFontSize() / DESIGN_BASE;
  return (designPx: number) => Math.round(designPx * scale * 100) / 100;
}
export const delay = (ms: number): Promise<void> => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

/**
 * 判断当前是否在每个月的x日内
 * @param num 判断日期
 * @returns 布尔值，是否在当前x日内
 */
export function isWithinXDays(num: number): boolean {
  const today = new Date();
  const currentDate = today.getDate(); // 获取当前日期
  // 确保num是有效日期（1到31之间的数）
  if (num < 1 || num > 31) {
    throw new Error('num must be between 1 and 31');
  }

  // 判断当前日期是否在num之前或等于num
  return currentDate <= num;
}
/*
 * 深克隆
 * @param obj 需要克隆的对象
 * @param hash WeakMap 用于检测循环引用
 * @returns 克隆后的对象
 */
export function deepClone<T>(obj: T, hash = new WeakMap()): T {
  // null 和 undefined 直接返回
  if (obj === null || obj === undefined) {
    return obj;
  }
  if (typeof obj !== 'object') {
    return obj;
  }

  // 检测循环引用
  if (hash.has(obj as any)) {
    return hash.get(obj as any);
  }

  // Date 对象
  if (obj instanceof Date) {
    const cloneDate = new Date(obj.getTime()) as T;
    hash.set(obj as any, cloneDate);
    return cloneDate;
  }

  // RegExp 对象
  if (obj instanceof RegExp) {
    const cloneRegExp = new RegExp(obj.source, obj.flags) as T;
    hash.set(obj as any, cloneRegExp);
    return cloneRegExp;
  }

  // Function 对象
  if (obj instanceof Function) {
    const cloneFunction = obj.bind({}) as T;
    hash.set(obj as any, cloneFunction);
    return cloneFunction;
  }

  // Map 对象
  if (obj instanceof Map) {
    const cloneMap = new Map() as T;
    hash.set(obj as any, cloneMap);
    (obj as Map<any, any>).forEach((value, key) => {
      (cloneMap as Map<any, any>).set(deepClone(key, hash), deepClone(value, hash));
    });
    return cloneMap;
  }

  // Set 对象
  if (obj instanceof Set) {
    const cloneSet = new Set() as T;
    hash.set(obj as any, cloneSet);
    (obj as Set<any>).forEach((value) => {
      (cloneSet as Set<any>).add(deepClone(value, hash));
    });
    return cloneSet;
  }

  // 数组
  if (Array.isArray(obj)) {
    const cloneArray = [] as T;
    hash.set(obj as any, cloneArray);
    (obj as any[]).forEach((item, index) => {
      (cloneArray as any[])[index] = deepClone(item, hash);
    });
    return cloneArray;
  }

  // 普通对象
  const cloneObj = Object.create(Object.getPrototypeOf(obj)) as T;
  hash.set(obj as any, cloneObj);

  // 处理 Symbol 类型的属性键
  const symbolKeys = Object.getOwnPropertySymbols(obj);
  symbolKeys.forEach((symbolKey) => {
    (cloneObj as any)[symbolKey] = deepClone((obj as any)[symbolKey], hash);
  });

  // 处理普通属性键
  Object.keys(obj as any).forEach((key) => {
    (cloneObj as any)[key] = deepClone((obj as any)[key], hash);
  });

  return cloneObj;
}

/**
 * 给颜色添加透明度
 * @param color 16进制颜色值 (例如 '#000000' 或 '#FFF')
 * @param alpha 透明度 (0 ~ 1)
 * @returns rgba 格式字符串
 */
export const addAlpha = (color: string, alpha: number): string => {
  // 基础容错
  if (!color) return '';

  // 如果是 Hex 颜色 (#000000)
  if (color.startsWith('#')) {
    let hex: string = color.replace('#', '');

    // 处理简写 Hex (#FFF -> #FFFFFF)
    if (hex.length === 3) {
      hex = hex
        .split('')
        .map((c) => c + c)
        .join('');
    }

    // 解析 RGB
    const bigint: number = parseInt(hex, 16);
    const r: number = (bigint >> 16) & 255;
    const g: number = (bigint >> 8) & 255;
    const b: number = bigint & 255;

    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  // 如果已经是 rgba，暂时直接返回
  return color;
};;
